import React from 'react';
import { Home, ShieldCheck } from 'lucide-react';

/**
 * Country compliance auto-detect block. Pure presentation — the
 * parent owns the `agreed` state and detection.
 *
 * Visible behaviour matches the legacy UnifiedAuthPanel exactly:
 *  - amber border + ring + pulsing house when un-ticked
 *  - emerald static once ticked
 *  - bold "← TICK THIS FIRST TO CONTINUE" hint when un-ticked
 *  - kept data-testid="compliance-block" for the shake-flash helper
 */
export const ComplianceBlock = ({ agreed, onToggle, countryName, regime, disclaimer }) => {
  const consentLabel = `I agree to Shopperden's Terms & Privacy Policy under ${regime || `${countryName} law`}, and I am 18+.`;

  return (
    <div
      className={`mb-5 rounded-lg border-2 px-4 py-3.5 transition-all duration-300 ${
        agreed
          ? 'border-emerald-300 bg-emerald-50/60'
          : 'border-amber-400 bg-amber-50 ring-4 ring-amber-100/80'
      }`}
      data-testid="compliance-block"
    >
      <label className="flex items-start gap-3 cursor-pointer">
        <span className="relative flex-shrink-0 mt-0.5 flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => onToggle(e.target.checked)}
            data-testid="compliance-checkbox"
            className="h-6 w-6 accent-[#10B981] cursor-pointer relative z-10"
          />
          {!agreed && (
            <span
              aria-hidden="true"
              className="absolute -inset-1 rounded-full bg-amber-400/60 animate-ping pointer-events-none"
              data-testid="compliance-pulse"
            />
          )}
          <Home
            aria-hidden="true"
            data-testid="compliance-house-blink"
            className={`h-5 w-5 transition-colors ${
              agreed ? 'text-emerald-500' : 'text-[#E07A3A] auth-house-blink'
            }`}
          />
        </span>
        <span className="text-sm text-slate-700 leading-relaxed">
          {!agreed && (
            <span
              className="block mb-1 text-[11px] uppercase tracking-[0.18em] font-bold text-amber-700"
              data-testid="compliance-hint"
            >
              ← Tick this first to continue
            </span>
          )}
          <ShieldCheck
            className={`inline h-4 w-4 mr-1 -mt-0.5 ${
              agreed ? 'text-emerald-600' : 'text-amber-600'
            }`}
          />
          <span className="font-semibold text-slate-800">
            {countryName} compliance auto-detected.
          </span>{' '}
          {consentLabel}
          {disclaimer && (
            <span className="block mt-1 text-xs text-slate-500">{disclaimer}</span>
          )}
        </span>
      </label>
    </div>
  );
};

export default ComplianceBlock;
